import { useEffect, useState, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import type { Thresholds, DiagnosticThresholds, LifecycleThresholds } from "../lib/types";
import { PageHeader, Card, Loading, ErrorNote, Spinner, Pill } from "../components/ui";
import { dateTime } from "../lib/format";

interface SettingsResp {
  thresholds: Thresholds;
  fxRates: Record<string, number>;
  branding: { logo: string | null; favicon: string | null };
  adFlagThresholds: { creativeFatigueDays: number; highFrequency: number };
  diagnosticThresholds: DiagnosticThresholds;
  lifecycleThresholds: LifecycleThresholds;
  conversionTypes: string[];
  metaSource: string;
}
interface Health {
  metaConfigured: boolean;
  metaSource: string;
}
interface SyncLogRow {
  id: string;
  status: string;
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  accounts_synced: number;
  metrics_upserted: number;
  error: string | null;
  details: {
    annotationsUpserted?: number;
    activitiesFetched?: number;
    annotationsError?: string | null;
    mode?: string;
    durationMs?: number;
    clientsFailed?: number;
    timings?: Array<{ client: string; ms: number; metrics: number; error?: string }>;
  } | null;
}

function fmtDuration(ms?: number): string {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export function Settings() {
  const { isOwner } = useAuth();
  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Branding, alert thresholds, exchange rates, Meta source, and sync history."
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <BrandingCard />
        <ThresholdsCard />
        <AdFlagsCard />
        <div className="lg:col-span-2">
          <DiagnosticsCard />
        </div>
        <LifecycleCard />
        <FxRatesCard />
        <MetaSourceCard />
        {isOwner && (
          <div className="lg:col-span-2">
            <AdminsCard />
          </div>
        )}
        <div className="lg:col-span-2">
          <SyncLogCard />
        </div>
      </div>
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Failed to read file"));
    r.readAsDataURL(file);
  });
}

function BrandingCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  const [logo, setLogo] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.branding) {
      setLogo(data.branding.logo);
      setFavicon(data.branding.favicon);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.patch("/api/settings/branding", { logo, favicon }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["branding"] }); // live favicon/logo refresh
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setErr((e as Error).message),
  });

  async function pick(e: ChangeEvent<HTMLInputElement>, kind: "logo" | "favicon") {
    setErr(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return setErr("Please choose an image file.");
    const maxKb = kind === "logo" ? 256 : 64;
    if (file.size > maxKb * 1024) return setErr(`${kind} must be ≤ ${maxKb}KB.`);
    const dataUrl = await readAsDataUrl(file);
    if (kind === "logo") setLogo(dataUrl);
    else setFavicon(dataUrl);
  }

  if (isLoading) return <Card><Loading /></Card>;
  if (error) return <Card><ErrorNote message={(error as Error).message} /></Card>;

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Branding</h2>
      <p className="mb-4 text-xs text-muted">
        App logo (sidebar + login) and browser favicon. PNG or SVG — logo ≤256KB, favicon ≤64KB.
        The title stays "LOAD STUDIOS / ADS DASHBOARD".
      </p>
      <div className="space-y-4">
        <BrandingField label="Logo" value={logo} onPick={(e) => pick(e, "logo")} onClear={() => setLogo(null)} />
        <BrandingField label="Favicon" value={favicon} onPick={(e) => pick(e, "favicon")} onClear={() => setFavicon(null)} />
      </div>
      {err && <div className="mt-3"><ErrorNote message={err} /></div>}
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Spinner className="h-4 w-4" />} Save branding
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function BrandingField({
  label,
  value,
  onPick,
  onClear,
}: {
  label: string;
  value: string | null;
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-hairline bg-surface-2">
          {value ? (
            <img src={value} alt={label} className="h-8 w-8 object-contain" />
          ) : (
            <span className="text-[10px] text-muted">none</span>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={onPick}
          className="min-w-0 flex-1 text-xs text-muted file:mr-2 file:rounded file:border-0 file:bg-surface-3 file:px-2 file:py-1 file:text-ink"
        />
        {value && (
          <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={onClear}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

interface AdminRow {
  id: string;
  email: string | null;
  full_name: string | null;
  is_owner: boolean;
}

function AdminsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admins"],
    queryFn: () => api.get<{ admins: AdminRow[] }>("/api/admins"),
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ user: { email: string }; tempPasswordSet: boolean }>("/api/admins", {
        email,
        password: password || undefined,
      }),
    onSuccess: (res) => {
      setErr(null);
      setMsg(
        res.tempPasswordSet
          ? `Created ${res.user.email} with a random password — send them a reset link.`
          : `Created ${res.user.email}.`
      );
      setEmail("");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["admins"] });
    },
    onError: (e) => setErr((e as Error).message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/api/admins/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Admin users</h2>
      <p className="mb-4 text-xs text-muted">
        Owner-only. New admins see all clients. Client logins are managed per client on the{" "}
        <Link to="/clients" className="text-brand">Clients</Link> screen.
      </p>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorNote message={(error as Error).message} />
      ) : (
        <ul className="mb-4 space-y-1 text-sm">
          {data!.admins.map((a) => (
            <li key={a.id} className="flex items-center justify-between rounded bg-surface-2 px-3 py-1.5">
              <span className="text-ink-secondary">
                {a.email ?? a.id}
                {a.is_owner && <span className="ml-2 text-[10px] uppercase text-brand">owner</span>}
              </span>
              {!a.is_owner && (
                <button
                  className="btn-danger px-2 py-0.5 text-xs"
                  onClick={() =>
                    confirm(`Remove admin ${a.email ?? a.id}? This deletes their login.`) &&
                    revoke.mutate(a.id)
                  }
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="space-y-3 border-t border-hairline pt-4"
      >
        <div className="label">Create an admin</div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input
            className="input"
            type="email"
            placeholder="admin@loadstudios.io"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className="input"
            type="text"
            placeholder="Temp password (optional, min 8)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && <ErrorNote message={err} />}
        {msg && <p className="text-xs text-good">{msg}</p>}
        <button type="submit" className="btn-primary" disabled={create.isPending}>
          {create.isPending && <Spinner className="h-4 w-4" />} Create admin
        </button>
      </form>
    </Card>
  );
}

function ThresholdsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  const [form, setForm] = useState<Thresholds>({ cpcRise: 0.15, convDrop: 0.15, rise: 0.15 });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.thresholds) setForm(data.thresholds);
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.patch("/api/settings/thresholds", form),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading) return <Card><Loading /></Card>;
  if (error) return <Card><ErrorNote message={(error as Error).message} /></Card>;

  const rows: { key: keyof Thresholds; label: string; hint: string }[] = [
    { key: "cpcRise", label: "Cost-per-conversion rise → Drop", hint: "Flag a drop when CPC rises by more than this." },
    { key: "convDrop", label: "Conversions fall → Drop", hint: "Flag a drop when conversions fall by more than this." },
    { key: "rise", label: "Strong improvement → Rising", hint: "Flag a riser when conversions climb or CPC falls by more than this." },
  ];

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Alert thresholds</h2>
      <p className="mb-4 text-xs text-muted">Week-over-week change that trips the drop / rise flags.</p>
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.key}>
            <label className="label">{r.label}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step={1}
                className="input tnum w-24"
                value={Math.round((form[r.key] ?? 0) * 100)}
                onChange={(e) => setForm((f) => ({ ...f, [r.key]: Number(e.target.value) / 100 }))}
              />
              <span className="text-sm text-muted">%</span>
            </div>
            <p className="mt-1 text-xs text-muted">{r.hint}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Spinner className="h-4 w-4" />} Save thresholds
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function AdFlagsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  const [fatigueDays, setFatigueDays] = useState("42");
  const [freq, setFreq] = useState("2.5");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.adFlagThresholds) {
      setFatigueDays(String(data.adFlagThresholds.creativeFatigueDays));
      setFreq(String(data.adFlagThresholds.highFrequency));
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch("/api/settings/ad-flags", {
        creativeFatigueDays: Number(fatigueDays),
        highFrequency: Number(freq),
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["clientDetail"] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading) return <Card><Loading /></Card>;
  if (error) return <Card><ErrorNote message={(error as Error).message} /></Card>;

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Ad health flags</h2>
      <p className="mb-4 text-xs text-muted">
        Deterministic thresholds behind the Creative Fatigue and High Frequency chips.
      </p>
      <div className="space-y-4">
        <div>
          <label className="label">Creative Fatigue — creative live longer than (days)</label>
          <input
            type="number"
            min={1}
            step={1}
            className="input tnum w-28"
            value={fatigueDays}
            onChange={(e) => setFatigueDays(e.target.value)}
          />
          <p className="mt-1 text-xs text-muted">
            Flags an active, still-spending ad whose creative has been live beyond this.
          </p>
        </div>
        <div>
          <label className="label">High Frequency — frequency over the window above</label>
          <input
            type="number"
            min={0}
            step={0.1}
            className="input tnum w-28"
            value={freq}
            onChange={(e) => setFreq(e.target.value)}
          />
          <p className="mt-1 text-xs text-muted">
            Frequency = impressions ÷ reach over the selected date window.
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Spinner className="h-4 w-4" />} Save thresholds
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function DiagnosticsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  // Fractions (deadband, fatigueTrendDrop) are edited as percentages.
  const [f, setF] = useState({
    deadband: "5",
    minImpressions: "500",
    minClicks: "20",
    minTrendDays: "4",
    fatigueTrendDrop: "15",
    wrongClicksCtrRatio: "1.2",
    wrongClicksConvRatio: "0.5",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const d = data?.diagnosticThresholds;
    if (d) {
      setF({
        deadband: String(Math.round(d.deadband * 100)),
        minImpressions: String(d.minImpressions),
        minClicks: String(d.minClicks),
        minTrendDays: String(d.minTrendDays),
        fatigueTrendDrop: String(Math.round(d.fatigueTrendDrop * 100)),
        wrongClicksCtrRatio: String(d.wrongClicksCtrRatio),
        wrongClicksConvRatio: String(d.wrongClicksConvRatio),
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch("/api/settings/diagnostics", {
        deadband: Number(f.deadband) / 100,
        minImpressions: Number(f.minImpressions),
        minClicks: Number(f.minClicks),
        minTrendDays: Number(f.minTrendDays),
        fatigueTrendDrop: Number(f.fatigueTrendDrop) / 100,
        wrongClicksCtrRatio: Number(f.wrongClicksCtrRatio),
        wrongClicksConvRatio: Number(f.wrongClicksConvRatio),
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["clientDetail"] });
      qc.invalidateQueries({ queryKey: ["diagnosticsConfig"] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading)
    return (
      <Card>
        <Loading />
      </Card>
    );
  if (error)
    return (
      <Card>
        <ErrorNote message={(error as Error).message} />
      </Card>
    );

  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Funnel diagnostics</h2>
      <p className="mb-4 text-xs text-muted">
        Tunable thresholds behind the CTR-based diagnostic pills (Upstream / Downstream / Wrong
        clicks / Fatigue Incoming). See the{" "}
        <Link to="/docs" className="text-brand hover:underline">
          Guide
        </Link>{" "}
        for what each one does.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DiagField label="Deadband (%)" hint="Ignore changes smaller than this" value={f.deadband} onChange={set("deadband")} step={1} />
        <DiagField label="Min impressions" hint="Below this = no pill" value={f.minImpressions} onChange={set("minImpressions")} step={50} />
        <DiagField label="Min clicks" hint="Below this = no pill" value={f.minClicks} onChange={set("minClicks")} step={5} />
        <DiagField label="Fatigue: CTR drop (%)" hint="Later half vs earlier half" value={f.fatigueTrendDrop} onChange={set("fatigueTrendDrop")} step={1} />
        <DiagField label="Fatigue: min days" hint="Data needed for the trend" value={f.minTrendDays} onChange={set("minTrendDays")} step={1} />
        <DiagField label="Wrong clicks: CTR ≥ ×median" hint="Relative to peers" value={f.wrongClicksCtrRatio} onChange={set("wrongClicksCtrRatio")} step={0.1} />
        <DiagField label="Wrong clicks: conv ≤ ×median" hint="Relative to peers" value={f.wrongClicksConvRatio} onChange={set("wrongClicksConvRatio")} step={0.05} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Spinner className="h-4 w-4" />} Save diagnostics
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function DiagField({
  label,
  hint,
  value,
  onChange,
  step,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  step: number;
}) {
  return (
    <div className="min-w-0">
      <label className="label">{label}</label>
      <input type="number" min={0} step={step} className="input tnum" value={value} onChange={onChange} />
      <p className="mt-1 text-[11px] text-muted">{hint}</p>
    </div>
  );
}

function LifecycleCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  const [f, setF] = useState({ newDays: "7", modifiedDays: "7", endingDays: "7" });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const l = data?.lifecycleThresholds;
    if (l) {
      setF({
        newDays: String(l.newDays),
        modifiedDays: String(l.modifiedDays),
        endingDays: String(l.endingDays),
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch("/api/settings/lifecycle", {
        newDays: Number(f.newDays),
        modifiedDays: Number(f.modifiedDays),
        endingDays: Number(f.endingDays),
      }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["clientDetail"] });
      qc.invalidateQueries({ queryKey: ["diagnosticsConfig"] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading)
    return (
      <Card>
        <Loading />
      </Card>
    );
  if (error)
    return (
      <Card>
        <ErrorNote message={(error as Error).message} />
      </Card>
    );

  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Lifecycle windows</h2>
      <p className="mb-4 text-xs text-muted">
        Day-windows behind the blue New / Modified / Ending soon context pills.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <DiagField label="New — within (days)" hint="Since creation" value={f.newDays} onChange={set("newDays")} step={1} />
        <DiagField label="Modified — within (days)" hint="Since last change" value={f.modifiedDays} onChange={set("modifiedDays")} step={1} />
        <DiagField label="Ending soon — within (days)" hint="Until schedule end" value={f.endingDays} onChange={set("endingDays")} step={1} />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending && <Spinner className="h-4 w-4" />} Save lifecycle
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function FxRatesCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResp>("/api/settings"),
  });
  const [usd, setUsd] = useState("");
  const [eur, setEur] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.fxRates) {
      setUsd(data.fxRates.USD != null ? String(data.fxRates.USD) : "");
      setEur(data.fxRates.EUR != null ? String(data.fxRates.EUR) : "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.patch("/api/settings/fx-rates", { USD: Number(usd), EUR: Number(eur) }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["clientDetail"] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading) return <Card><Loading /></Card>;
  if (error) return <Card><ErrorNote message={(error as Error).message} /></Card>;

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Exchange rates → MUR</h2>
      <p className="mb-4 text-xs text-muted">
        Meta spends in each ad account's own currency. These rates convert spend to MUR so ROAS
        (revenue is always MUR) and the cross-client total are correct. Update when rates move.
      </p>
      <div className="space-y-4">
        {[
          { code: "USD", label: "1 USD = ? MUR", value: usd, set: setUsd },
          { code: "EUR", label: "1 EUR = ? MUR", value: eur, set: setEur },
        ].map((r) => (
          <div key={r.code}>
            <label className="label">{r.label}</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted">Rs</span>
              <input
                type="number"
                min={0}
                step="0.01"
                className="input tnum w-32"
                value={r.value}
                onChange={(e) => r.set(e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          className="btn-primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || !usd || !eur}
        >
          {save.isPending && <Spinner className="h-4 w-4" />} Save rates
        </button>
        {saved && <span className="text-xs text-good">Saved</span>}
      </div>
    </Card>
  );
}

function MetaSourceCard() {
  const health = useQuery({ queryKey: ["health"], queryFn: () => api.get<Health>("/health") });
  const [tools, setTools] = useState<string[] | null>(null);
  const test = useMutation({
    mutationFn: () => api.get<{ tools: string[] }>("/api/meta/debug/tools"),
    onSuccess: (r) => setTools(r.tools),
  });

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-ink">Meta data source</h2>
      <p className="mb-4 text-xs text-muted">
        Swappable adapter — Pipeboard today, direct Meta API later. Tokens live server-side only.
      </p>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted">Adapter</span>
          <Pill tone="brand">{health.data?.metaSource ?? "…"}</Pill>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted">Configured</span>
          {health.data?.metaConfigured ? (
            <Pill tone="good">connected</Pill>
          ) : (
            <Pill>token not set</Pill>
          )}
        </div>
      </div>

      <div className="mt-4">
        <button className="btn-ghost" onClick={() => test.mutate()} disabled={test.isPending}>
          {test.isPending && <Spinner className="h-4 w-4" />} Test connection
        </button>
        {test.isError && <div className="mt-2"><ErrorNote message={(test.error as Error).message} /></div>}
        {tools && (
          <p className="mt-2 text-xs text-good">
            Connected. Tools: {tools.length ? tools.join(", ") : "(none reported)"}
          </p>
        )}
      </div>

      <p className="mt-4 border-t border-hairline pt-3 text-xs text-muted">
        Client logins are managed per client on the{" "}
        <Link to="/clients" className="text-brand">Clients</Link> screen.
      </p>
    </Card>
  );
}

function SyncLogCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["syncLog"],
    queryFn: () => api.get<{ log: SyncLogRow[] }>("/api/sync/log"),
    refetchInterval: 15_000,
  });

  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold text-ink">Recent syncs</h2>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorNote message={(error as Error).message} />
      ) : (data?.log.length ?? 0) === 0 ? (
        <p className="text-sm text-muted">No syncs recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2 font-medium">Started</th>
                <th className="px-3 py-2 font-medium">Trigger</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Duration</th>
                <th className="px-3 py-2 text-right font-medium">Accounts</th>
                <th className="px-3 py-2 text-right font-medium">Metrics</th>
                <th className="px-3 py-2 text-right font-medium">Markers</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {data!.log.map((s) => (
                <tr key={s.id} className="border-b border-hairline/60 last:border-0">
                  <td className="px-3 py-2 tnum text-ink-secondary">{dateTime(s.started_at)}</td>
                  <td className="px-3 py-2 capitalize text-muted">
                    {s.triggered_by}
                    {s.details?.mode && (
                      <span className="ml-1 text-[10px] uppercase text-muted">· {s.details.mode}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        s.status === "success"
                          ? "text-good"
                          : s.status === "error"
                            ? "text-critical"
                            : "text-muted"
                      }
                    >
                      {s.status}
                    </span>
                  </td>
                  <td
                    className="px-3 py-2 text-right tnum text-muted"
                    title={
                      s.details?.timings
                        ? s.details.timings
                            .slice()
                            .sort((a, b) => b.ms - a.ms)
                            .slice(0, 5)
                            .map((t) => `${t.client}: ${fmtDuration(t.ms)}${t.error ? " (failed)" : ""}`)
                            .join("\n")
                        : undefined
                    }
                  >
                    {fmtDuration(s.details?.durationMs)}
                  </td>
                  <td className="px-3 py-2 text-right tnum">{s.accounts_synced}</td>
                  <td className="px-3 py-2 text-right tnum">{s.metrics_upserted}</td>
                  <td
                    className={`px-3 py-2 text-right tnum ${
                      s.details?.annotationsError ? "text-critical" : "text-muted"
                    }`}
                    title={
                      s.details?.annotationsError
                        ? `Activity log error: ${s.details.annotationsError}`
                        : s.details?.activitiesFetched != null
                          ? `${s.details.activitiesFetched} activity-log rows fetched`
                          : "no activity data (check META_ADAPTER=meta)"
                    }
                  >
                    {s.details?.annotationsError ? "!" : s.details?.annotationsUpserted ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-critical">
                    {s.error ??
                      [
                        s.details?.clientsFailed ? `${s.details.clientsFailed} client(s) failed` : "",
                        s.details?.annotationsError ? `annotations: ${s.details.annotationsError}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
